import {
  FaFutbol,
  FaCheckDouble,
  FaBolt,
  FaCalendarDay,
  FaCalendarWeek,
  FaListOl,
  FaBullseye,
  FaCheck,
} from 'react-icons/fa';

const currentYear = new Date().getFullYear();

export const landings = [
  // ==================== LEAGUES ====================
  {
    slug: 'premier-league-predictions',
    type: 'league',
    apiFilter: { league: 'EPL,ENGLAND_PREMIER_LEAGUE' },
    title: 'Premier League Predictions Today',
    subtitle: 'EPL fixtures today: picks, previews, and odds angles',
    seo: {
      title: 'EPL Predictions Today | Premier League Tips | Main-Bet',
      description: 'Free EPL predictions today and this weekend: Premier League picks, match previews, and odds angles for every fixture. Not a sure win.'
    },
    infoTitle: 'About Our Premier League Predictions',
    infoText: `The English Premier League is one of the most followed football competitions in the world, featuring elite clubs like Manchester City, Arsenal, Liverpool and Chelsea. We break down every EPL matchday using recent form, fixture congestion, injury news, home and away splits, and head-to-head context. Whether you are targeting match winners, goals markets, or BTTS angles, this page surfaces practical picks for today's Premier League fixtures.`,
    faq: [
      {
        q: 'How do you pick Premier League matches?',
        a: 'We look at team form, injury news, head-to-head records, and tactical context to highlight useful picks across match result, goals, and BTTS markets.'
      },
      {
        q: 'When are Premier League predictions published?',
        a: 'We publish our EPL predictions 24-48 hours before each matchday, giving you time to compare odds and place informed bets.'
      },
      {
        q: 'Do you cover all Premier League matches?',
        a: 'Yes, we provide predictions for every single Premier League fixture throughout the season, including midweek rounds and rescheduled games.'
      }
    ]
  },
  {
    slug: 'la-liga-predictions',
    type: 'league',
    apiFilter: { league: 'SPAIN_LA_LIGA' },
    title: 'La Liga Predictions Today',
    subtitle: 'La Liga fixtures: previews and goals market angles',
    seo: {
      title: `La Liga Predictions Today (${currentYear}) | Betting Tips, Picks & Previews`,
      description: `La Liga predictions and Spanish football betting tips for today, with tactical previews, goals market angles and value picks for Real Madrid, Barcelona, Atletico and more.`
    },
    infoTitle: 'About Our La Liga Predictions',
    infoText: `La Liga is home to some of the biggest clubs in football, including Real Madrid, Barcelona, and Atletico Madrid. We approach each fixture through tactical matchups, possession trends, recent form, expected-goals signals, and squad news. That makes this page especially useful for bettors looking beyond basic 1X2 picks and into goals markets, narrow-margin wins, and value opportunities across the Spanish top flight.`,
    faq: [
      {
        q: 'What markets do you cover for La Liga?',
        a: 'We cover match result (1X2), over/under goals, both teams to score, correct score, and special markets for every La Liga match.'
      },
      {
        q: 'Are La Liga predictions available for all matchdays?',
        a: 'Yes, we provide predictions for every La Liga round, including midweek fixtures and derby matches throughout the season.'
      },
      {
        q: 'How do you analyze La Liga matches?',
        a: 'We combine statistical models with tactical analysis, considering team form, head-to-head records, injury reports, home/away performance, and managerial strategies.'
      }
    ]
  },
  {
    slug: 'serie-a-predictions',
    type: 'league',
    apiFilter: { league: 'ITALY_SERIE_A' },
    title: 'Serie A Predictions Today',
    subtitle: 'Serie A fixtures today: tactical previews and scoring angles',
    seo: {
      title: `Serie A Predictions Today (${currentYear}) | Italian Betting Tips & Picks`,
      description: `Serie A predictions and Italian football betting tips for today, with tactical previews, low-scoring edges, and picks for Inter, Juventus, Milan, Napoli and more.`
    },
    infoTitle: 'About Our Serie A Predictions',
    infoText: `Italian Serie A blends tactical discipline with elite attacking quality, creating a very different betting environment from the Premier League or Bundesliga. Clubs like Inter Milan, AC Milan, Juventus, and Napoli regularly produce matches where structure, game state, and defensive detail matter as much as raw talent. Our Serie A page focuses on those edges, highlighting match previews, likely tempo, scoring ranges, and value plays for today's Italian fixtures.`,
    faq: [
      {
        q: 'What makes Serie A predictions different?',
        a: 'Serie A is known for its tactical complexity and strong defensive traditions. Our analysis accounts for these unique characteristics, including formation changes, set-piece threats, and home/away disparities.'
      },
      {
        q: 'Do you cover Coppa Italia matches?',
        a: 'Our primary focus is Serie A league matches, but we also cover major Coppa Italia fixtures involving top-tier clubs.'
      },
      {
        q: 'How often are Serie A predictions updated?',
        a: 'Predictions are published before each matchday and updated if there are significant team news changes or late injury reports.'
      }
    ]
  },
  {
    slug: 'bundesliga-predictions',
    type: 'league',
    apiFilter: { league: 'GERMANY_BUNDESLIGA' },
    title: 'Bundesliga Predictions Today',
    subtitle: 'Bundesliga fixtures: goals, BTTS, and totals angles',
    seo: {
      title: `Bundesliga Predictions Today (${currentYear}) | Goals, BTTS & Betting Tips`,
      description: `Bundesliga predictions and German football betting tips for today, featuring high-scoring match previews, BTTS angles and picks for Bayern, Dortmund, Leverkusen and more.`
    },
    infoTitle: 'About Our Bundesliga Predictions',
    infoText: `The Bundesliga is widely regarded as one of Europe's most entertaining leagues for goals-focused bettors. Bayern Munich, Borussia Dortmund, Bayer Leverkusen, and RB Leipzig headline a competition known for aggressive pressing, transition-heavy football, and strong home atmospheres. Our Bundesliga predictions are tailored to that profile, with particular attention on over and under lines, BTTS setups, and attacking matchups that can produce value quickly.`,
    faq: [
      {
        q: 'Why is the Bundesliga good for over/under bets?',
        a: 'The Bundesliga often ranks among the highest-scoring leagues in Europe, which makes over 2.5 goals and BTTS markets especially attractive when the matchup and team news support it.'
      },
      {
        q: 'Do you provide Bundesliga 2 predictions?',
        a: 'Our main focus is on Bundesliga 1, but we occasionally cover high-profile 2. Bundesliga matches and promotion/relegation fixtures.'
      },
      {
        q: 'When is the best time to bet on Bundesliga matches?',
        a: 'We recommend checking our predictions 24 hours before kickoff, as team lineups and final injury reports are usually confirmed by then.'
      }
    ]
  },
  {
    slug: 'ligue-1-predictions',
    type: 'league',
    apiFilter: { league: 'FRANCE_LIGUE_1,FRANCE_LIGUE_ONE' },
    title: 'Ligue 1 Predictions Today',
    subtitle: 'Ligue 1 fixtures today: form, xG, and value picks',
    seo: {
      title: `Ligue 1 Predictions Today (${currentYear}) | Picks, Odds & Betting Tips`,
      description: `Ligue 1 predictions and French football betting tips for today, with xG-driven previews, value bets and match analysis for PSG, Marseille, Monaco, Lyon and more.`
    },
    infoTitle: 'About Our Ligue 1 Predictions',
    infoText: `Ligue 1 combines elite-level clubs, fast-rising young talent, and a deep mid-table where odds can be mispriced. PSG drives much of the headline attention, but the real betting value often appears across fixtures involving Marseille, Monaco, Lyon, Lille, and the broader European-race and relegation battles. Our Ligue 1 predictions lean on form analysis, expected-goals data, squad depth, and situational angles to uncover useful picks across the French top division.`,
    faq: [
      {
        q: 'Is Ligue 1 predictable for betting?',
        a: 'While PSG often dominates, the rest of Ligue 1 is highly competitive. Our analysis identifies value in matches throughout the table, not just the top fixtures.'
      },
      {
        q: 'What data do you use for Ligue 1 predictions?',
        a: 'We use expected goals (xG), shots on target, possession stats, head-to-head records, and current form to generate our Ligue 1 predictions.'
      },
      {
        q: 'Do you cover Coupe de France matches?',
        a: 'We primarily focus on Ligue 1, but cover major cup fixtures featuring top-division teams.'
      }
    ]
  },
  {
    slug: 'champions-league-predictions',
    type: 'league',
    apiFilter: { league: 'UCL,UEFA_CHAMPS_LEAGUE' },
    title: 'Champions League Predictions Today',
    subtitle: 'UCL fixtures: knockout previews and market angles',
    seo: {
      title: `Champions League Predictions Today (${currentYear}) | UCL Tips, Picks & Odds`,
      description: `Champions League predictions and UCL betting tips, covering league-phase fixtures, knockout ties, goals markets and big-match previews across Europe's top clubs.`
    },
    infoTitle: 'About Our Champions League Predictions',
    infoText: `The UEFA Champions League is the highest level of European club football, bringing together the continent's strongest teams in a competition where small tactical edges matter. From the league phase to the knockout rounds, every UCL match attracts global attention and heavy betting volume. Our Champions League predictions combine domestic form, European pedigree, rotation risk, travel impact, and tactical matchup analysis to help identify value in the biggest fixtures of the season.`,
    faq: [
      {
        q: 'How do you predict Champions League matches?',
        a: 'We analyze domestic form, European pedigree, head-to-head records in continental competition, travel fatigue, and squad rotation patterns to build our UCL match picks.'
      },
      {
        q: 'Do you cover Champions League qualifiers?',
        a: 'Yes, we cover the full Champions League campaign from qualifying rounds through to the final, including the new league-phase format.'
      },
      {
        q: 'Are Champions League predictions harder than league matches?',
        a: 'UCL matches can be less predictable due to the intensity of knockout football, but our models account for the unique dynamics of European competition.'
      }
    ]
  },
  {
    slug: 'europa-league-predictions',
    type: 'league',
    apiFilter: { league: 'UEL,UEFA_EUROPA_LEAGUE' },
    title: 'Europa League Predictions Today',
    subtitle: 'Europa League fixtures: rotation-aware picks for today',
    seo: {
      title: `Europa League Predictions Today (${currentYear}) | UEL Betting Tips & Picks`,
      description: `Europa League predictions and UEL betting tips for today's fixtures, with rotation analysis, value bets and match previews across Europe's second-tier competition.`
    },
    infoTitle: 'About Our Europa League Predictions',
    infoText: `The UEFA Europa League regularly produces some of the best value spots in European football because squad rotation, travel, and motivation can shift prices quickly. While it sits below the Champions League in profile, the competition still features major clubs, difficult away trips, and knockout ties where game management becomes crucial. Our Europa League predictions focus on those edges, combining form, scheduling pressure, lineup expectations, and matchup dynamics for every round.`,
    faq: [
      {
        q: 'Is the Europa League worth betting on?',
        a: 'Absolutely. The Europa League often provides better value odds than the Champions League, as bookmakers may underestimate lesser-known teams or overlook squad rotation patterns.'
      },
      {
        q: 'Do teams prioritize the Europa League?',
        a: 'It varies. Some clubs field strong lineups while others rotate. Our predictions account for each team\'s likely approach based on their domestic league position and recent squad management.'
      },
      {
        q: 'Do you cover the Conference League too?',
        a: 'Our primary focus is on the Europa League, but we cover high-profile Conference League matches involving well-known clubs.'
      }
    ]
  },
  {
    slug: 'world-cup-2026-predictions',
    type: 'league',
    inNav: false,
    apiFilter: { league: 'FIFA_WORLD_CUP,WORLD_CUP_2026,FIFA_WC_2026' },
    title: 'World Cup 2026 Predictions',
    subtitle: 'World Cup 2026 hub: tournament picks and outright angles',
    seo: {
      title: `World Cup 2026 Predictions | Betting Tips, Outright Picks & Guide`,
      description: `World Cup 2026 predictions, outright betting tips, tournament analysis and match previews for the FIFA World Cup in the USA, Mexico and Canada.`
    },
    infoTitle: 'About Our World Cup 2026 Predictions',
    infoText: `The 2026 FIFA World Cup will be the biggest in tournament history, running from 11 June to 19 July 2026 across 16 host cities in the United States, Mexico, and Canada. With 48 teams and 104 matches, the expanded format creates more betting angles than any previous World Cup, from outright winner markets to group-stage matchups and knockout-round betting. This page is designed as a central hub for World Cup 2026 predictions, outright tips, tournament context, and match-by-match analysis as the event approaches and begins.`,
    faq: [
      {
        q: 'When does the 2026 World Cup start?',
        a: 'The 2026 FIFA World Cup starts on 11 June 2026 and runs until 19 July 2026, with matches played across 16 host cities in the USA, Mexico, and Canada.'
      },
      {
        q: 'How will the 48-team format affect predictions?',
        a: 'The 48-team format means 12 groups, more total matches, and a wider spread in team strength. That creates more early-stage mismatch analysis, more outright betting angles, and a different path to the knockout rounds than previous World Cups.'
      },
      {
        q: 'Will you cover World Cup qualifiers?',
        a: 'Yes. In the build-up to the finals, this page can support World Cup-related content such as outright markets, tournament guides, and key international previews, before shifting to match-by-match predictions once the finals begin.'
      }
    ]
  },

  // ==================== BETTING TYPES ====================
  {
    slug: 'over-under-predictions',
    type: 'betType',
    apiFilter: { tipFilter: 'over,under' },
    icon: FaFutbol,
    title: 'Over/Under 2.5 Goals Predictions',
    subtitle: "Today's totals picks: over and under 2.5 goal lines",
    seo: {
      title: 'Over/Under Predictions Today | Football Totals Tips | Main-Bet',
      description: "Over/under football predictions today: totals tips across the major leagues. Over 2.5 and under 2.5 picks, not match winners."
    },
    infoTitle: 'What Are Over/Under 2.5 Goals Predictions?',
    infoText: `Over/Under 2.5 goals is one of the most searched and most bet football markets because it focuses on game profile rather than final winner. An "Over 2.5" selection lands when a match finishes with at least three total goals, while "Under 2.5" lands when there are two or fewer. Our page filters for matches where scoring averages, defensive records, tempo, team news, and historical goal patterns point toward a strong totals angle, helping bettors find cleaner setups across multiple leagues.`,
    faq: [
      {
        q: 'Where are over under football predictions?',
        a: 'On this page. Today’s over/under football predictions are the published totals tips: over or under 2.5, not 1X2 or BTTS.'
      },
      {
        q: 'What does Over 2.5 goals mean?',
        a: 'Over 2.5 goals means you are betting that there will be 3 or more goals scored in total during the match (by both teams combined). Under 2.5 means 2 or fewer total goals.'
      },
      {
        q: 'Which leagues are best for Over 2.5 goals?',
        a: 'The Bundesliga, Eredivisie, and many South American leagues consistently produce high-scoring matches. However, value can be found in any league when the right conditions align.'
      },
      {
        q: 'How do you predict over/under outcomes?',
        a: 'We analyze team scoring averages, defensive strength, head-to-head goal records, recent form, home/away goal trends, and situational factors like squad rotation and match importance.'
      },
      {
        q: 'Is Over 2.5 goals a safe bet?',
        a: 'No single bet is guaranteed, but Over 2.5 is popular because it removes the need to predict a winner. Our analysis focuses on identifying matches where the data strongly supports a particular outcome.'
      }
    ]
  },
  {
    slug: 'btts-predictions',
    type: 'betType',
    apiFilter: { tipFilter: 'btts,both teams,[1-9]-[1-9]' },
    icon: FaCheckDouble,
    title: 'Both Teams to Score (BTTS) Predictions',
    subtitle: "Today's BTTS picks: both sides to score setups",
    seo: {
      title: `BTTS Predictions Today (${currentYear}) | Both Teams To Score Tips & Picks`,
      description: `BTTS predictions for today's football matches, featuring both-teams-to-score tips, scoring trend analysis and picks across the major football leagues.`
    },
    infoTitle: 'What Are BTTS (Both Teams to Score) Predictions?',
    infoText: `Both Teams to Score (BTTS) is a straightforward football betting market built around one question: will each side score at least once? BTTS: Yes lands when both teams find the net, while BTTS: No lands when one side keeps a clean sheet or the match ends 0-0. Our BTTS page looks at scoring consistency, defensive weaknesses, clean-sheet rates, home and away splits, and game-state tendencies to identify strong both-teams-to-score opportunities across the major leagues.`,
    faq: [
      {
        q: 'What does BTTS mean in betting?',
        a: 'BTTS stands for "Both Teams to Score." You bet on whether both teams will score at least one goal each during the 90 minutes (plus stoppage time). It doesn\'t matter who wins.'
      },
      {
        q: 'How do you predict BTTS outcomes?',
        a: 'We look at each team\'s scoring record, goals conceded, clean sheet percentage, head-to-head goal trends, and attacking/defensive form to determine BTTS probability.'
      },
      {
        q: 'Can I combine BTTS with other markets?',
        a: 'Yes, BTTS is commonly combined with match result (e.g., Home Win & BTTS) or over/under goals for higher-odds bets. Our predictions can help you build informed accumulators.'
      },
      {
        q: 'Which leagues have the highest BTTS percentage?',
        a: 'Leagues like the Bundesliga, Eredivisie, and Australian A-League historically have high BTTS percentages. However, we identify BTTS value across all leagues we cover.'
      }
    ]
  },
  {
    slug: 'high-odds-predictions',
    type: 'betType',
    apiFilter: { minOdds: 2.5 },
    icon: FaBolt,
    title: 'High Odds Predictions Today',
    subtitle: 'Longshot and underdog picks - every tip priced at 2.50 or higher',
    seo: {
      title: 'High Odds Predictions Today | Longshots and Underdog Tips',
      description: 'Football tips priced at 2.50 or higher: underdogs, draws and 3.00+ longshots from today\'s published selections.'
    },
    infoTitle: 'What Are High Odds Predictions?',
    infoText: `This list is a price cut, not a "risky match" label. We keep a published tip only if the selected decimal odd is 2.50 or higher (about 40% implied chance or less). That is usually an underdog to win, a draw, or a longer goals price. Short favourites live on the sure page instead. These picks miss more often than 1.40 bankers; when they land the payout is larger. Stake small and treat the board as singles, not a five-fold.`,
    faq: [
      {
        q: 'What odds count as high odds on this page?',
        a: 'The selected price must be 2.50 or higher. 3.00+ is a true longshot: lower hit rate, bigger payout when it lands.'
      },
      {
        q: 'Are high odds the same as longshots or underdogs?',
        a: 'For searchers, yes. High odds, longshot and underdog all mean you are not backing a short favourite. This page is that filter.'
      },
      {
        q: 'Should I put high odds tips into an accumulator?',
        a: 'You can, but several 2.50+ legs stack variance fast. Build structured accas on the Accumulators page. Here, keep them as small singles unless you accept the extra risk.'
      },
      {
        q: 'How should I stake high odds football tips?',
        a: 'Smaller than a 1.50 favourite. A losing run is normal at these prices. Do not raise the stake to chase a miss.'
      }
    ]
  },
  {
    slug: 'tomorrow-predictions',
    type: 'betType',
    inNav: false,
    apiFilter: { datePreset: 'tomorrow' },
    icon: FaCalendarDay,
    title: 'Football Predictions Tomorrow',
    subtitle: "Tomorrow's fixtures - picks for the next calendar day",
    seo: {
      title: "Football Predictions Tomorrow | Tips for Tomorrow's Matches",
      description: "Tips for tomorrow's football fixtures: match previews and picks for games that kick off the next day, not today's remaining slate."
    },
    infoTitle: "Tomorrow's Football Predictions",
    infoText: `Home is for today. This URL is the next calendar day only, so you can build a slip the night before without scrolling past kick-offs that have already started. Lineups and prices still move; use it as a first pass, then check team news closer to the whistle. If you want Friday to Sunday in one list, use the weekend page instead.`,
    faq: [
      {
        q: 'What does "tomorrow" mean on this page?',
        a: "The next calendar day in our prediction timezone. Today's remaining kick-offs stay on the home feed."
      },
      {
        q: 'When do tomorrow\'s tips go live?',
        a: 'When we publish them, usually 24-48 hours before kick-off. A thin list in the morning often fills in later the same day.'
      },
      {
        q: "Can I still bet today's matches from here?",
        a: "No. Use the home page for today. This URL is only for tomorrow's fixtures."
      },
      {
        q: 'How is this different from weekend predictions?',
        a: 'Tomorrow is one day. Weekend is Friday through Sunday of the current or next weekend.'
      }
    ]
  },
  {
    slug: 'weekend-predictions',
    type: 'betType',
    inNav: false,
    apiFilter: { datePreset: 'weekend' },
    icon: FaCalendarWeek,
    title: 'Weekend Football Predictions',
    subtitle: 'Friday to Sunday fixtures in one list',
    seo: {
      title: 'Weekend Football Predictions | Friday to Sunday Tips',
      description: 'Friday, Saturday and Sunday football tips in one list. Weekend fixtures only - no Tuesday or Wednesday midweek round.'
    },
    infoTitle: 'Weekend Football Predictions',
    infoText: `Most betting volume sits on Friday night, Saturday afternoon and Sunday. This page is that block: Friday through Sunday of this weekend, or the coming one if today is Monday to Thursday. Midweek Champions League rounds stay off this list on purpose. Early in the week the Friday games may not be published yet, so the page can look empty until those tips go live.`,
    faq: [
      {
        q: 'Which days count as the weekend here?',
        a: "Friday, Saturday and Sunday. Monday to Thursday you get the coming weekend; Friday to Sunday you get this one, including games that have already finished."
      },
      {
        q: 'Do you cover Friday night games?',
        a: 'Yes. European leagues often kick off on Friday, not only Saturday at 15:00.'
      },
      {
        q: 'Why is the list empty on a Monday?',
        a: "We only publish a few days ahead. If Friday is not in the feed yet, this page waits until those tips go live."
      },
      {
        q: 'Is this the same as tomorrow\'s predictions?',
        a: "No. Tomorrow is a single day. Weekend is the Friday-Sunday block."
      }
    ]
  },
  {
    slug: '1x2-predictions',
    type: 'betType',
    inNav: false,
    apiFilter: { tipFilter: 'to win,draw' },
    icon: FaListOl,
    title: '1X2 Predictions Today',
    subtitle: 'Match result tips - home win, draw or away win',
    seo: {
      title: '1X2 Predictions Today | Home, Draw, Away Tips',
      description: '1X2 tips for today: home win, draw or away win. Match-result picks only - not Over/Under 2.5 or BTTS.'
    },
    infoTitle: 'What Are 1X2 Predictions?',
    infoText: `1 is home win, X is draw, 2 is away win. This page keeps tips written as a team to win or a draw, including scoreline wording such as "Arsenal to Win 2-1" or "Draw 1-1". Over 2.5, Under 2.5 and BTTS have their own pages and are filtered out here. A short-priced home win is still a 1X2 bet, not a "sure" one.`,
    faq: [
      {
        q: 'What does 1X2 mean?',
        a: '1 home win, X draw, 2 away win. Full time after 90 minutes plus stoppage, unless the market says extra time.'
      },
      {
        q: 'Do you include Over/Under or BTTS here?',
        a: 'No. Those pages exist separately. This list is match result only.'
      },
      {
        q: 'Why do some 1X2 tips show a scoreline?',
        a: 'We often publish the pick as a score, for example "Arsenal to Win 2-1". The priced selection is still the match result (or that score when we publish one).'
      },
      {
        q: 'Is 1X2 safer than other markets?',
        a: 'Not by itself. A 1.30 favourite can still lose. Check the price against the match, not the market name.'
      }
    ]
  },
  {
    slug: 'correct-score-predictions',
    type: 'betType',
    apiFilter: { tipFilter: '[0-9]-[0-9]' },
    icon: FaBullseye,
    title: 'Correct Score Predictions Today',
    subtitle: "Today's scoreline picks, not a pick for every fixture",
    seo: {
      title: 'Correct Score Predictions Today | Scoreline Tips | Main-Bet',
      description: "Today's correct score football predictions. Scoreline picks where we published one, not a guaranteed score, and not a pick for every match. 18+ only."
    },
    infoTitle: 'What Are Correct Score Predictions Today?',
    infoText: `This page lists today's published scoreline tips, for example "Arsenal to Win 2-1" or "Draw 1-1". We do not invent a correct score for every fixture. If a match has no useful scoreline angle, it stays off this list. Correct score is a high-variance market; treat it as information, not a sure win.`,
    faq: [
      {
        q: 'Do you have a correct score pick for every match?',
        a: 'No. This feed only includes tips that already include a scoreline. We skip fixtures where a score guess is just noise.'
      },
      {
        q: 'Is a correct score prediction a sure win?',
        a: 'No. Scoreline markets miss more often than match-result tips. Stake small. 18+ only.'
      },
      {
        q: 'How is this different from 1X2 predictions?',
        a: '1X2 is home, draw, or away. This page is the subset written as a score, such as 2-1 or 0-0.'
      }
    ]
  },
  {
    slug: 'sure-predictions',
    type: 'betType',
    apiFilter: { maxOdds: 1.5 },
    icon: FaCheck,
    title: 'Sure Football Predictions Today',
    subtitle: 'Shorter-priced tips - 1.50 or below, not guaranteed wins',
    seo: {
      title: 'Sure Football Predictions Today | Short-Odds Tips | Main-Bet',
      description: 'Short-odds football tips at 1.50 or below. Not a guaranteed win - you bet at your own risk. 18+ only.'
    },
    notice:
      'There is no such thing as a 100% sure football prediction. These are shorter-priced tips (1.50 or below), not guaranteed wins. You bet at your own risk. Gamble responsibly - 18+ only.',
    infoTitle: 'What Are "Sure" Predictions?',
    infoText: `"Sure win" search usually means a short favourite, not a fixed match. We do not sell guarantees. This page is the opposite of high odds: selected price 1.50 or lower (about 67% implied chance or more). A 1.40 home win can still lose to a red card or a late equaliser. Stake only what you can afford to lose.`,
    faq: [
      {
        q: 'Do you publish 100 sure football predictions?',
        a: 'People search for 100 sure football predictions. We do not publish 100 guaranteed winners. This page is shorter-priced tips (1.50 or below), not a numbered list of sure wins.'
      },
      {
        q: 'Are these tips 100% sure?',
        a: 'No. Nothing in football is 100% sure. This page only lists shorter prices (1.50 or below). You bet at your own risk.'
      },
      {
        q: 'How do you pick what counts as "sure"?',
        a: 'We do not score a secret confidence field. We filter by the decimal odd of the published selection: 1.50 or lower. That is a price cutoff, not a guarantee.'
      },
      {
        q: 'Why can a "sure" tip still lose?',
        a: 'A 1.40 favourite is priced to win often, not always. Injuries, red cards, and variance still happen. Never stake money you cannot afford to lose.'
      },
      {
        q: 'Is this the same as high-odds predictions?',
        a: 'It is the opposite filter. High odds starts at 2.50. This page caps at 1.50. Same published tips, different price band.'
      }
    ]
  }
];

export const getLandingsByType = (type) =>
  landings.filter((l) => l.type === type && l.inNav !== false);

/** Related chips: all same type except World Cup (inNav false on leagues). Bet-type hubs stay in this list even when hidden from the menu. */
export const getRelatedLandings = (type, slug) =>
  landings.filter((l) => {
    if (l.type !== type || l.slug === slug) return false;
    if (type === 'league') return l.inNav !== false;
    return true;
  });

/** First league id from apiFilter.league (comma-separated aliases). */
export const getLandingLeagueId = (landing) => {
  const raw = landing?.apiFilter?.league;
  if (!raw || typeof raw !== 'string') return null;
  const first = raw.split(',')[0].trim();
  return first || null;
};
